import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { SprintAPI } from '../utils/api';
import { useAuth } from '../components/AuthProvider';
import { isTransientNetworkError } from '../utils/auth';
import { flushPendingTaskAdds, mergePendingIntoSprint } from '../utils/pendingSprintTaskAdds';
import { migrateSprintTasks } from '../utils/migrations/backfillTaskDates';
import { migrateSprintEstimatedDuration } from '../utils/taskDuration';
import { migrateNumericDependsOn } from '../utils/migrations/migrateNumericDependsOn';
import { applyPendingSprintRename, mergeLoadedSprint } from '../utils/pendingSprintRenames';

/** Sentinel: previous workspace not yet recorded (first load after mount). */
const WORKSPACE_LOAD_UNSET = Symbol('workspaceLoadUnset');

const isWorkspaceSwitch = (prior, next) =>
  prior !== WORKSPACE_LOAD_UNSET && prior !== next;

/**
 * Helper to check if an item is a template
 */
const isTemplateItem = (item) => item.isTemplate === true || item.type === 'template';

/**
 * useDataLoader Hook
 * 
 * Handles initial data loading and provides methods to load full sprint/template data.
 * Also handles migrations for backwards compatibility (e.g., backfilling task dates).
 * 
 * UNIFIED DATA MODEL:
 * - All items (campaigns AND templates) are stored in a single `sprints` array
 * - Templates are distinguished by `isTemplate: true` flag
 * - This allows all campaign logic (nesting, trash, etc.) to work for templates automatically
 * - The `templates` getter provides filtered access for UI components that need it
 */
export function useDataLoader() {
  const { workspaceId, loading: authLoading } = useAuth();

  // Single unified array for all items (campaigns + templates)
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const prevWorkspaceIdRef = useRef(WORKSPACE_LOAD_UNSET);
  // Monotonic id per list load: a response from a superseded load (e.g. the
  // old workspace's list resolving after a switch) must never touch state.
  const loadSeqRef = useRef(0);
  
  // Ref to access current sprints in callbacks without re-creating them
  const sprintsRef = useRef(sprints);
  useEffect(() => {
    sprintsRef.current = sprints;
  }, [sprints]);

  // Ids whose full GET failed. A tab click selects the campaign before this
  // returns; without the record, MainContent would spin the loader forever.
  const inflightLoadsRef = useRef(new Map());
  const [itemLoadErrors, setItemLoadErrors] = useState({});

  const clearItemLoadError = useCallback((id) => {
    setItemLoadErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const beginItemLoad = useCallback((id) => {
    const inflight = inflightLoadsRef.current;
    inflight.set(id, (inflight.get(id) || 0) + 1);
    clearItemLoadError(id);
  }, [clearItemLoadError]);

  const endItemLoad = useCallback((id, error) => {
    const inflight = inflightLoadsRef.current;
    const left = (inflight.get(id) || 1) - 1;
    if (left > 0) {
      inflight.set(id, left);
      if (!error) clearItemLoadError(id);
      return;
    }
    inflight.delete(id);
    if (error) {
      const message = error.message || 'Failed to load';
      setItemLoadErrors((prev) => (
        prev[id] === message ? prev : { ...prev, [id]: message }
      ));
    } else {
      clearItemLoadError(id);
    }
  }, [clearItemLoadError]);

  // Live workspace for pinning per-item loads started before a switch
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;

  // Derived: templates are items with isTemplate flag (excluding trashed)
  // This is a computed view, not separate state
  const templates = useMemo(() => 
    sprints.filter(item => isTemplateItem(item) && !item.deletedAt),
    [sprints]
  );

  // Derived: campaigns are items WITHOUT isTemplate flag
  const campaigns = useMemo(() => 
    sprints.filter(s => !isTemplateItem(s)),
    [sprints]
  );

  // Load when workspace is ready and whenever it changes (X-Workspace-Id drives API partition)
  useEffect(() => {
    if (authLoading) return;

    const seq = ++loadSeqRef.current;

    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      const priorWs = prevWorkspaceIdRef.current;
      const wsChanged = isWorkspaceSwitch(priorWs, workspaceId);
      prevWorkspaceIdRef.current = workspaceId;
      if (wsChanged) {
        inflightLoadsRef.current.clear();
        setItemLoadErrors({});
      }

      for (;;) {
        if (cancelled || seq !== loadSeqRef.current) return;
        try {
          // Replay missions whose PUT was aborted by a refresh
          await flushPendingTaskAdds(workspaceId);
          if (cancelled || seq !== loadSeqRef.current) return;

          // Fetch ALL items from API, pinned to this effect's workspace
          const allItems = await SprintAPI.list('all', { workspaceId });
          // A newer load (workspace switch) started while this one was in
          // flight — its items belong to another workspace, drop them.
          if (cancelled || seq !== loadSeqRef.current) return;
          console.log('[useDataLoader] Load complete, items:', allItems?.length, 'workspace:', workspaceId);

          // Store everything in one array
          // IMPORTANT: Preserve any fully-loaded sprints that already have tasks (same workspace only)
          setSprints((prev) => {
            if (prev.length === 0 || wsChanged) {
              return allItems || [];
            }
            const merged = (allItems || []).map((newItem) => {
              const existing = prev.find((p) => p.id === newItem.id);
              if (existing && Array.isArray(existing.tasks)) {
                console.log('[useDataLoader] Preserving full sprint:', existing.id);
                return existing;
              }
              return newItem;
            });
            return merged;
          });
          setLoading(false);
          return;
        } catch (error) {
          if (cancelled || seq !== loadSeqRef.current) return;
          // A laptop wake fails this fetch once, then the network returns.
          // Keep the loading overlay up and try again instead of wiping the list.
          if (isTransientNetworkError(error)) {
            console.warn('[useDataLoader] Network down, retrying campaign list:', error);
            await new Promise((resolve) => setTimeout(resolve, 1500));
            continue;
          }
          console.error('Failed to load data:', error);
          setSprints([]);
          setLoading(false);
          return;
        }
      }
    };
    loadData();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, authLoading]);

  // Load full sprint data if we only have metadata
  // First checks if sprint is already fully loaded in local state (has tasks array)
  // This prevents race conditions where API data is stale due to throttled saves
  const loadFullSprint = useCallback(async (sprintId) => {
    // First, check if we already have full data in local state
    // A sprint is "fully loaded" if it has a tasks array (even if empty)
    // This prevents race conditions where API data is stale due to throttled saves
    const existingSprint = sprintsRef.current.find(s => s.id === sprintId);
    if (existingSprint && Array.isArray(existingSprint.tasks)) {
      clearItemLoadError(sprintId);
      const loaded = mergePendingIntoSprint(workspaceIdRef.current, existingSprint);
      const named = mergeLoadedSprint(loaded, null);
      if (named !== loaded) {
        setSprints((prev) => prev.map((s) => (
          s.id === sprintId ? mergeLoadedSprint(s, null) : s
        )));
      }
      return named;
    }
    
    // Pin to the workspace active now; if the user switches while this is in
    // flight, the result must not be injected into the other workspace's list
    const workspaceAtCall = workspaceIdRef.current;

    beginItemLoad(sprintId);
    let failed = null;
    let dropped = false;
    try {
      let fullSprint = await SprintAPI.get(sprintId, { workspaceId: workspaceAtCall });

      if (!fullSprint) {
        failed = new Error("Couldn't load this campaign");
        return null;
      }

      {
        // Keep the requested id so list → detail matching can't miss on data.id drift
        fullSprint = mergePendingIntoSprint(workspaceAtCall, {
          ...fullSprint,
          id: fullSprint.id || sprintId,
          tasks: Array.isArray(fullSprint.tasks) ? fullSprint.tasks : [],
        });

        const alreadyLoaded = sprintsRef.current.find(
          (s) => (s.id === sprintId || s.id === fullSprint.id) && Array.isArray(s.tasks)
        );
        if (alreadyLoaded) {
          // Server copy is stale relative to the in-memory campaign. Still let a
          // matching server name retire the pending rename.
          applyPendingSprintRename(fullSprint, { fromServer: true });
          const kept = mergeLoadedSprint(alreadyLoaded, null);
          if (kept !== alreadyLoaded) {
            setSprints((prev) => {
              const idx = prev.findIndex((s) => s.id === sprintId || s.id === fullSprint.id);
              if (idx === -1) return prev;
              const merged = mergeLoadedSprint(prev[idx], null);
              if (merged === prev[idx]) return prev;
              const next = prev.slice();
              next[idx] = merged;
              return next;
            });
          }
          return kept;
        }

        // Keep a mission-rename that hasn't been saved onto this record yet,
        // including any migration write below. fromServer clears the mark only
        // when the stored name already matches — an overlay must not.
        fullSprint = applyPendingSprintRename(fullSprint, { fromServer: true });

        // Run migration for task dates (backwards compatibility)
        const { sprint: migratedSprint, migrated } = migrateSprintTasks(fullSprint, fullSprint.statuses);
        if (migrated) {
          fullSprint = migratedSprint;
          // Save the migrated sprint back to API
          try {
            await SprintAPI.update(fullSprint.id, fullSprint, { workspaceId: workspaceAtCall });
            console.log('💾 Saved migrated sprint to API');
          } catch (saveError) {
            console.warn('Failed to save migrated sprint:', saveError);
          }
        }

        const dur = migrateSprintEstimatedDuration(fullSprint);
        if (dur.migrated) {
          fullSprint = dur.sprint;
          try {
            await SprintAPI.update(fullSprint.id, fullSprint, { workspaceId: workspaceAtCall });
            console.log('💾 Saved sprint after estimated-duration migration');
          } catch (saveError) {
            console.warn('Failed to save duration-migrated sprint:', saveError);
          }
        }

        const deps = migrateNumericDependsOn(fullSprint);
        if (deps.migrated) {
          fullSprint = deps.sprint;
          try {
            await SprintAPI.update(fullSprint.id, fullSprint, { workspaceId: workspaceAtCall });
            console.log('💾 Saved sprint after numeric dependsOn migration');
          } catch (saveError) {
            console.warn('Failed to save deps-migrated sprint:', saveError);
          }
        }
        
        // Workspace changed while this load was in flight — the sprint belongs
        // to the previous workspace, don't inject it into the current list.
        if (workspaceIdRef.current !== workspaceAtCall) {
          dropped = true;
          return null;
        }
        
        setSprints(prev => {
          const idx = prev.findIndex(s => s.id === sprintId || s.id === fullSprint.id);
          if (idx === -1) return [...prev, fullSprint];
          const current = prev[idx];
          // A full copy arrived while this fetch was in flight (nested rename).
          // Don't replace its tasks with this response.
          if (current && Array.isArray(current.tasks)) {
            const merged = mergeLoadedSprint(current, null);
            if (merged === current) return prev;
            const next = prev.slice();
            next[idx] = merged;
            return next;
          }
          const next = prev.slice();
          next[idx] = fullSprint;
          return next;
        });
        return fullSprint;
      }
    } catch (error) {
      failed = error;
      console.error('Failed to load sprint data:', error);
      return null;
    } finally {
      endItemLoad(sprintId, dropped ? null : failed);
    }
  }, [beginItemLoad, endItemLoad, clearItemLoadError]);

  // Load full template data if we only have metadata
  // Now uses the same sprints array since templates are unified
  const loadFullTemplate = useCallback(async (templateId) => {
    // First, check if we already have full data in local state
    const existingTemplate = sprintsRef.current.find(s => s.id === templateId);
    if (existingTemplate && Array.isArray(existingTemplate.tasks)) {
      clearItemLoadError(templateId);
      return existingTemplate;
    }
    
    // Pin to the workspace active now (see loadFullSprint)
    const workspaceAtCall = workspaceIdRef.current;

    beginItemLoad(templateId);
    let failed = null;
    let dropped = false;
    try {
      let fullTemplate = await SprintAPI.get(templateId, { workspaceId: workspaceAtCall });
      if (!fullTemplate) {
        failed = new Error("Couldn't load this template");
        return null;
      }
      {
        // Run migration for task dates (backwards compatibility)
        const { sprint: migratedTemplate, migrated } = migrateSprintTasks(fullTemplate, fullTemplate.statuses);
        if (migrated) {
          fullTemplate = migratedTemplate;
          try {
            await SprintAPI.update(fullTemplate.id, fullTemplate, { workspaceId: workspaceAtCall });
            console.log('💾 Saved migrated template to API');
          } catch (saveError) {
            console.warn('Failed to save migrated template:', saveError);
          }
        }

        const durT = migrateSprintEstimatedDuration(fullTemplate);
        if (durT.migrated) {
          fullTemplate = durT.sprint;
          try {
            await SprintAPI.update(fullTemplate.id, fullTemplate, { workspaceId: workspaceAtCall });
            console.log('💾 Saved template after estimated-duration migration');
          } catch (saveError) {
            console.warn('Failed to save duration-migrated template:', saveError);
          }
        }

        const depsT = migrateNumericDependsOn(fullTemplate);
        if (depsT.migrated) {
          fullTemplate = depsT.sprint;
          try {
            await SprintAPI.update(fullTemplate.id, fullTemplate, { workspaceId: workspaceAtCall });
            console.log('💾 Saved template after numeric dependsOn migration');
          } catch (saveError) {
            console.warn('Failed to save deps-migrated template:', saveError);
          }
        }
        
        // Workspace changed while this load was in flight — drop the result
        if (workspaceIdRef.current !== workspaceAtCall) {
          dropped = true;
          return null;
        }
        
        // Update in the unified sprints array
        setSprints(prev => prev.map(s => s.id === fullTemplate.id ? fullTemplate : s));
        return fullTemplate;
      }
    } catch (error) {
      failed = error;
      console.error('Failed to load template data:', error);
      return null;
    } finally {
      endItemLoad(templateId, dropped ? null : failed);
    }
  }, [beginItemLoad, endItemLoad, clearItemLoadError]);

  // Get first active sprint (for auto-selection)
  // Excludes trashed campaigns and templates
  const getFirstActiveSprint = useCallback(() => {
    return sprints.find(s => s.status === 'active' && !s.deletedAt && !isTemplateItem(s)) || null;
  }, [sprints]);

  // For backwards compatibility, provide a setTemplates that updates the unified array
  // This allows existing code to work while we transition
  const setTemplates = useCallback((updater) => {
    setSprints(prev => {
      // Get current templates
      const currentTemplates = prev.filter(isTemplateItem);
      // Get non-templates
      const nonTemplates = prev.filter(s => !isTemplateItem(s));
      
      // Apply the updater to templates
      const newTemplates = typeof updater === 'function' 
        ? updater(currentTemplates)
        : updater;
      
      // Merge back
      return [...nonTemplates, ...newTemplates];
    });
  }, []);

  return {
    // Unified data
    sprints,
    setSprints,
    
    // Derived views for templates (computed, not separate state)
    templates,
    setTemplates, // Wrapper that updates the unified array
    
    // Derived view for campaigns only (excludes templates)
    campaigns,
    
    loading,
    itemLoadErrors,
    loadFullSprint,
    loadFullTemplate,
    getFirstActiveSprint,
  };
}

